import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { 
  Plus, 
  Folder, 
  LogOut, 
  Users, 
  Clock, 
  ChevronRight,
  Zap,
  X,
  Edit,
  Check,
  Mail,
  CheckSquare
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";

const Dashboard = () => {
  const { user, logout, setUser } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newProject, setNewProject] = useState({ name: "", description: "" });
  const [creating, setCreating] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [newName, setNewName] = useState("");
  const [sendingTestEmail, setSendingTestEmail] = useState(false);
  const [editingProjectId, setEditingProjectId] = useState(null);
  const [editingProjectName, setEditingProjectName] = useState("");

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`, { withCredentials: true });
      setProjects(response.data);
    } catch (error) {
      console.error("Failed to fetch projects:", error);
      toast.error("Failed to load projects");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProject = async (e) => {
    e.preventDefault();
    if (!newProject.name.trim()) {
      toast.error("Project name required");
      return;
    }

    setCreating(true);
    try {
      const response = await axios.post(`${API}/projects`, newProject, { withCredentials: true });
      setProjects([...projects, response.data]);
      setShowCreateModal(false);
      setNewProject({ name: "", description: "" });
      toast.success("Project initialized successfully");
    } catch (error) {
      console.error("Failed to create project:", error);
      toast.error("Failed to create project");
    } finally {
      setCreating(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const handleUpdateName = async () => {
    if (!newName.trim() || newName === user?.name) {
      setEditingName(false);
      return;
    }
    
    try {
      const response = await axios.put(`${API}/auth/profile`, { name: newName }, { withCredentials: true });
      setUser(response.data);
      toast.success("Name updated successfully");
      setEditingName(false);
    } catch (error) {
      toast.error("Failed to update name");
    }
  };

  const startEditingName = () => {
    setNewName(user?.name || "");
    setEditingName(true);
  };

  const handleTestEmail = async () => {
    setSendingTestEmail(true);
    try {
      const response = await axios.post(`${API}/test-email`, {}, { withCredentials: true });
      toast.success(`Test email sent to ${response.data.recipient}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send test email");
    } finally {
      setSendingTestEmail(false);
    }
  };

  const handleUpdateProjectName = async (projectId) => {
    if (!editingProjectName.trim()) {
      toast.error("Project name is required");
      return;
    }
    
    try {
      const response = await axios.put(
        `${API}/projects/${projectId}`,
        { name: editingProjectName },
        { withCredentials: true }
      );
      setProjects(projects.map(p => p.project_id === projectId ? response.data : p));
      setEditingProjectId(null);
      setEditingProjectName("");
      toast.success("Project name updated");
    } catch (error) {
      toast.error("Failed to update project name");
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric"
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stark-void flex items-center justify-center">
        <div className="text-center">
          <div className="arc-loader mx-auto mb-6"></div>
          <p className="font-display text-arc-cyan uppercase tracking-widest text-sm">
            Loading projects...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stark-void">
      {/* Background Grid */}
      <div className="fixed inset-0 opacity-5 pointer-events-none">
        <div className="absolute inset-0" style={{
          backgroundImage: `
            linear-gradient(rgba(0, 240, 255, 0.2) 1px, transparent 1px),
            linear-gradient(90deg, rgba(0, 240, 255, 0.2) 1px, transparent 1px)
          `,
          backgroundSize: '40px 40px'
        }} />
      </div>

      {/* Header */}
      <header className="glass border-b border-arc-cyan/20 px-6 py-4 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 border-2 border-arc-cyan flex items-center justify-center glow-cyan">
              <Zap className="w-5 h-5 text-arc-cyan" />
            </div>
            <div>
              <h1 className="font-display text-lg text-arc-cyan uppercase tracking-widest">
                VISTA
              </h1>
              <p className="font-mono text-[10px] text-arc-cyan/50">Visual Integrated System for Total Awareness</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-3 glass px-4 py-2 border border-arc-cyan/20">
              {user?.picture ? (
                <img 
                  src={user.picture} 
                  alt={user.name} 
                  className="w-8 h-8 border border-arc-cyan"
                />
              ) : (
                <div className="w-8 h-8 bg-arc-cyan/20 border border-arc-cyan flex items-center justify-center">
                  <span className="text-arc-cyan font-display text-sm">
                    {user?.name?.charAt(0) || "U"}
                  </span>
                </div>
              )}
              {editingName ? (
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleUpdateName()}
                    data-testid="edit-name-input"
                    className="bg-stark-void border border-arc-cyan/30 text-holo-white px-2 py-1 text-sm w-32 focus:border-arc-cyan outline-none"
                    autoFocus
                  />
                  <button
                    onClick={handleUpdateName}
                    data-testid="save-name-btn"
                    className="p-1 text-green-400 hover:bg-green-400/10"
                  >
                    <Check className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setEditingName(false)}
                    className="p-1 text-muted-foreground hover:bg-muted/10"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <span className="font-mono text-sm text-holo-white hidden sm:block">
                    {user?.name || "User"}
                  </span>
                  <button
                    onClick={startEditingName}
                    data-testid="edit-name-btn"
                    className="p-1 text-arc-cyan/50 hover:text-arc-cyan transition-colors"
                    title="Edit name"
                  >
                    <Edit className="w-3 h-3" />
                  </button>
                </div>
              )}
            </div>
            <button
              onClick={handleTestEmail}
              disabled={sendingTestEmail}
              data-testid="test-email-btn"
              className="p-2 border border-jarvis-amber/50 text-jarvis-amber hover:bg-jarvis-amber/10 transition-colors disabled:opacity-50"
              title="Send test email"
            >
              <Mail className="w-5 h-5" />
            </button>
            <button
              onClick={handleLogout}
              data-testid="logout-btn"
              className="p-2 border border-stark-red/50 text-stark-red hover:bg-stark-red/10 transition-colors"
              title="Logout"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8 relative z-10">
        {/* Section Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="font-display text-2xl text-holo-white uppercase tracking-wider mb-1">
              Projects
            </h2>
            <p className="font-mono text-xs text-arc-cyan/60">
              {projects.length} active project{projects.length !== 1 ? 's' : ''}
            </p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            data-testid="create-project-btn"
            className="flex items-center gap-2 px-4 py-2 bg-arc-cyan/10 border border-arc-cyan text-arc-cyan font-display text-xs uppercase tracking-widest transition-all duration-300 hover:bg-arc-cyan hover:text-stark-void glow-cyan-hover clip-chamfer"
          >
            <Plus className="w-4 h-4" />
            <span>New Project</span>
          </button>
        </div>

        {/* Projects Grid */}
        {projects.length === 0 ? (
          <div className="glass border border-arc-cyan/20 p-12 text-center">
            <Folder className="w-16 h-16 text-arc-cyan/30 mx-auto mb-4" />
            <p className="font-display text-lg text-holo-white uppercase tracking-wider mb-2">
              No Projects Found
            </p>
            <p className="text-muted-foreground text-sm mb-6">
              Initialize your first project to begin task coordination
            </p>
            <button
              onClick={() => setShowCreateModal(true)}
              data-testid="create-first-project-btn"
              className="inline-flex items-center gap-2 px-6 py-3 bg-arc-cyan/10 border border-arc-cyan text-arc-cyan font-display text-xs uppercase tracking-widest transition-all duration-300 hover:bg-arc-cyan hover:text-stark-void"
            >
              <Plus className="w-4 h-4" />
              <span>Create Project</span>
            </button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map((project) => (
              <ProjectCard 
                key={project.project_id} 
                project={project}
                onClick={() => navigate(`/project/${project.project_id}`)}
                formatDate={formatDate}
                isEditing={editingProjectId === project.project_id}
                editingName={editingProjectName}
                onStartEdit={() => {
                  setEditingProjectId(project.project_id);
                  setEditingProjectName(project.name);
                }}
                onCancelEdit={() => {
                  setEditingProjectId(null);
                  setEditingProjectName("");
                }}
                onNameChange={(name) => setEditingProjectName(name)}
                onSaveName={() => handleUpdateProjectName(project.project_id)}
              />
            ))}
          </div>
        )}
      </main>

      {/* Create Project Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="bg-stark-carbon border border-arc-cyan/30 text-holo-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-xl uppercase tracking-wider text-arc-cyan">
              Initialize Project
            </DialogTitle>
            <p className="sr-only">Create a new project with a name and description</p>
          </DialogHeader>
          <form onSubmit={handleCreateProject} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="font-mono text-xs uppercase tracking-wider text-arc-cyan/80">
                Project Name
              </Label>
              <Input
                value={newProject.name}
                onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
                placeholder="Enter project codename"
                data-testid="project-name-input"
                className="bg-stark-void border-arc-cyan/30 text-holo-white placeholder:text-muted-foreground focus:border-arc-cyan"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-mono text-xs uppercase tracking-wider text-arc-cyan/80">
                Description
              </Label>
              <Textarea
                value={newProject.description}
                onChange={(e) => setNewProject({ ...newProject, description: e.target.value })}
                placeholder="Project objectives and scope"
                data-testid="project-description-input"
                className="bg-stark-void border-arc-cyan/30 text-holo-white placeholder:text-muted-foreground focus:border-arc-cyan min-h-[100px]"
              />
            </div>
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowCreateModal(false)}
                className="flex-1 border-muted-foreground/30 text-muted-foreground hover:bg-muted/10"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={creating}
                data-testid="submit-project-btn"
                className="flex-1 bg-arc-cyan/10 border border-arc-cyan text-arc-cyan hover:bg-arc-cyan hover:text-stark-void font-display uppercase tracking-wider"
              >
                {creating ? "Initializing..." : "Create"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const ProjectCard = ({ project, onClick, formatDate, isEditing, editingName, onStartEdit, onCancelEdit, onNameChange, onSaveName }) => (
  <div
    data-testid={`project-card-${project.project_id}`}
    className="w-full text-left bg-cyan-900/30 border border-arc-cyan/40 p-6 hud-corners transition-all duration-300 hover:border-arc-cyan hover:bg-cyan-800/40 group task-card"
  >
    <div className="flex items-start justify-between mb-4">
      <div className="w-10 h-10 bg-arc-cyan/10 border border-arc-cyan/30 flex items-center justify-center">
        <Folder className="w-5 h-5 text-arc-cyan" />
      </div>
      <div className="flex items-center gap-2">
        {!isEditing && (
          <button
            onClick={(e) => { e.stopPropagation(); onStartEdit(); }}
            className="p-1 text-arc-cyan/50 hover:text-arc-cyan transition-colors opacity-0 group-hover:opacity-100"
            title="Edit project name"
            data-testid={`edit-project-name-${project.project_id}`}
          >
            <Edit className="w-4 h-4" />
          </button>
        )}
        <button
          onClick={onClick}
          className="text-arc-cyan/50 group-hover:text-arc-cyan transition-all"
        >
          <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-all" />
        </button>
      </div>
    </div>
    
    {isEditing ? (
      <div className="mb-4" onClick={(e) => e.stopPropagation()}>
        <Input
          value={editingName}
          onChange={(e) => onNameChange(e.target.value)}
          className="bg-stark-void border-arc-cyan/30 text-holo-white mb-2"
          autoFocus
          data-testid="edit-project-name-input"
        />
        <div className="flex gap-2">
          <Button
            size="sm"
            onClick={onSaveName}
            className="bg-arc-cyan/10 border border-arc-cyan text-arc-cyan hover:bg-arc-cyan hover:text-stark-void text-xs"
            data-testid="save-project-name-btn"
          >
            <Check className="w-3 h-3 mr-1" /> Save
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={onCancelEdit}
            className="text-muted-foreground hover:text-holo-white text-xs"
          >
            <X className="w-3 h-3 mr-1" /> Cancel
          </Button>
        </div>
      </div>
    ) : (
      <h3 
        className="font-display text-lg text-holo-white uppercase tracking-wider mb-2 cursor-pointer"
        onClick={onClick}
      >
        {project.name}
      </h3>
    )}
    
    {project.description && (
      <p className="text-sm text-muted-foreground line-clamp-2 mb-4 cursor-pointer" onClick={onClick}>
        {project.description}
      </p>
    )}
    
    <div className="flex items-center gap-4 text-xs font-mono">
      <div className="flex items-center gap-1 text-arc-cyan/60">
        <Users className="w-3 h-3" />
        <span>{project.team_members?.length || 1}</span>
      </div>
      <div className="flex items-center gap-1 text-green-400">
        <CheckSquare className="w-3 h-3" />
        <span>{project.active_task_count || 0} active</span>
      </div>
      <div className="flex items-center gap-1 text-red-400">
        <Check className="w-3 h-3" />
        <span>{project.completed_task_count || 0} done</span>
      </div>
    </div>
  </div>
);

export default Dashboard;
